import type { ApiCompetition, CompetitionVisibilityCode, CreateConsolationRequest } from './types';

/**
 * Model formularza turnieju pocieszenia.
 *
 * Organizator wybiera turniej zasadniczy i podaje nazwę, widoczność oraz daty —
 * skład (przegrani 1. rundy) i drabinkę backend buduje automatycznie, bez
 * losowania, w kolejności meczów turnieju zasadniczego.
 */
export interface ConsolationDraft {
  /** Identyfikator turnieju zasadniczego (źródła przegranych 1. rundy). */
  sourceId: string;
  name: string;
  visibility: CompetitionVisibilityCode;
  startDate: string;
  endDate: string;
  /**
   * Czy dołączyć też przegranych z 2. rundy. Wymaga zakończonej 2. rundy
   * turnieju zasadniczego (weryfikuje backend — 409 z komunikatem).
   */
  includeSecondRound: boolean;
}

/** Pusty formularz pocieszenia — bez wybranego źródła i nazwy. */
export const INITIAL_CONSOLATION_DRAFT: ConsolationDraft = {
  sourceId: '',
  name: '',
  visibility: 'PUBLIC',
  startDate: '',
  endDate: '',
  includeSecondRound: false,
};

/**
 * Turnieje, z których można utworzyć pocieszenie: wyłącznie system drabinki
 * (PLAYOFF) i rozgrywka co najmniej po losowaniu (w toku albo zakończona).
 * Zakończenie pierwszej rundy weryfikuje backend — lista rozgrywek nie zna
 * wyników meczów, więc dopiero próba utworzenia zwróci 409 z komunikatem.
 */
export function eligibleConsolationSources(
  competitions: readonly ApiCompetition[],
): ApiCompetition[] {
  return competitions.filter(
    (competition) =>
      competition.system === 'PLAYOFF' &&
      (competition.status === 'IN_PROGRESS' || competition.status === 'COMPLETED'),
  );
}

/** Zwraca komunikat błędu albo `null`, gdy formularz pocieszenia da się wysłać. */
export function validateConsolationDraft(draft: ConsolationDraft): string | null {
  if (!draft.sourceId) {
    return 'Wybierz turniej zasadniczy.';
  }
  if (!draft.name.trim()) {
    return 'Podaj nazwę turnieju pocieszenia.';
  }
  if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
    return 'Data zakończenia nie może być wcześniejsza niż data rozpoczęcia.';
  }
  return null;
}

/** Składa żądanie `POST /api/competitions/{sourceId}/consolation` z formularza. */
export function buildCreateConsolationPayload(draft: ConsolationDraft): CreateConsolationRequest {
  return {
    name: draft.name.trim(),
    visibility: draft.visibility,
    startDate: draft.startDate || null,
    endDate: draft.endDate || null,
    includeSecondRound: draft.includeSecondRound,
  };
}
